import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  X,
  Check,
  Eye,
  Loader2,
  Download,
  Image as ImageIcon,
  ClipboardCheck,
  Inbox,
  Send,
  Palette,
  FileText,
  Search,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  AlertCircle,
  FileSignature,
} from "lucide-react";
import { Drawer } from "@material-tailwind/react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import axiosInstance from "../../services/api/axios";
import { useUser } from "../../context/UserContext";
import { CloseButton, PrimaryButton, OutlineButton } from "../../components/Buttons";
import useSubscriptionFeatures from "../../hooks/useSubscriptionFeatures";
import DemandLetterApproval from "../Customers/DemandLetterApproval";
import AgreementRequestsList from "../../components/AgreementRequestsList";
import {
  normalizeMediaBySection,
  getSectionDisplayOrder,
} from "../../utils/designSubmissionMedia";
import { getPresignedDownloadUrls } from "../../utils/fileUtils";
import { getMediaUrl } from "../../utils/media";
import usePagination from "../../hooks/usePagination";
import PaginationControls from "../../components/PaginationControls";

/* ---------- formatters ---------- */
const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v)}`;
};
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const STATUS_BADGE = {
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-rose-50 text-rose-700 border-rose-200",
};
const DESIGN_STATUS_LABEL = {
  DEVELOPER_PENDING: "PENDING",
  DEVELOPER_APPROVED: "APPROVED",
  DEVELOPER_REJECTED: "REJECTED",
};

// Adapt a lead detail (flat lead + deliverables[]) to the legacy "design approval" shape the
// developer-approval screen expects. The finalized/latest deliverable supplies the design fields;
// `developerApprovalStatus` is derived from the lead's single lifecycle status.
const adaptLeadToApproval = (lead, devStatus) => {
  const dels = lead?.deliverables || [];
  const finalIter =
    dels.find((d) => d.designIterationId === lead?.finalizedIterationId) || dels[dels.length - 1] || {};
  return {
    ...finalIter,
    ...lead,
    developerApprovalStatus: devStatus,
    idLeadId: lead?.interiorDesignerLeadId,
    designIterationId: finalIter.designIterationId,
    iterationNumber: finalIter.iterationNumber,
    media: finalIter.media,
    buyerFeedback: finalIter.buyerFeedback,
    estimatedPrice: finalIter.estimatedPrice,
    isFinal: finalIter.isFinal,
    designerNotes: finalIter.designerNotes,
  };
};

// All possible tabs. `feature` marks the subscription flag that gates each
// tab — design approvals stay available on both plans, demand-letter and
// generic CRM approvals (inbox/sent are sales-flavoured) are only visible
// on Unified. Filtered at render time by `useApprovalTabs()`.
const ALL_TABS = [
  {
    label: "Inbox",
    value: "approvals",
    icon: Inbox,
    description: "Items waiting on your decision",
    feature: "approvals.crm",
  },
  {
    label: "Sent",
    value: "requests",
    icon: Send,
    description: "Approval requests you've raised",
    feature: "approvals.crm",
  },
  {
    label: "Designs",
    value: "designs",
    icon: Palette,
    description: "Interior design iterations to review",
    feature: "approvals.design",
  },
  {
    label: "Demand letters",
    value: "demandletter",
    icon: FileText,
    description: "Demand letter approvals",
    feature: "approvals.crm",
  },
  {
    label: "Interior Design Executions",
    value: "interior_executions",
    icon: FileSignature,
    description: "Interior execution agreements to sign",
  },
];

const STATUS_FILTERS = [
  { value: "ALL", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

/* ---------- reusable bits ---------- */
const KpiTile = ({ icon: Icon, label, value, subtitle, accent = "slate" }) => {
  const accents = {
    flamingo: "bg-flamingo-50 text-flamingo-600",
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-600",
    indigo: "bg-indigo-50 text-indigo-600",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

const TileSkeleton = () => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 h-32">
    <Skeleton circle height={40} width={40} />
    <Skeleton height={12} width="70%" className="mt-3" />
    <Skeleton height={28} width="50%" className="mt-2" />
  </div>
);

const StatusPill = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
      STATUS_BADGE[status] || "bg-slate-100 text-slate-600 border-slate-200"
    }`}
  >
    {status === "PENDING" ? (
      <Clock size={11} />
    ) : status === "APPROVED" ? (
      <CheckCircle2 size={11} />
    ) : status === "REJECTED" ? (
      <XCircle size={11} />
    ) : null}
    {status}
  </span>
);

/* ---------- main component ---------- */
function Approval() {
  const { userInfo } = useUser();
  const { hasFeature } = useSubscriptionFeatures();
  const [searchParams] = useSearchParams();

  // Filter the tab list to what the tenant's plan unlocks. Plus tenants see
  // Designs only; Unified sees everything.
  const TABS = useMemo(
    () => ALL_TABS.filter((tab) => !tab.feature || hasFeature(tab.feature)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
  const loginId = userInfo?.loginId;

  // Default to the first tab the plan actually unlocks. Plus tenants land
  // on "designs" since CRM tabs are filtered out.
  const [activeTab, setActiveTab] = useState(TABS[0]?.value || "designs");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);

  /* ---------- drawer / selection ---------- */
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCostSheetModalOpen, setIsCostSheetModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [comment, setComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [designMediaUrls, setDesignMediaUrls] = useState({});
  const [designMediaUrlsLoading, setDesignMediaUrlsLoading] = useState(false);

  /* ---------- project picker (shared with dashboard) ---------- */
  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get("/dashboard/projects")
      .then((res) => {
        if (mounted) setProjectOptions(Array.isArray(res) ? res : res?.items || []);
      })
      .catch(() => {
        if (mounted) setProjectOptions([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  /* ---------- summary KPIs ---------- */
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setSummaryLoading(true);
    const params = {};
    if (selectedProjectId) params.projectId = selectedProjectId;
    axiosInstance
      .get("/approvals/summary", { params })
      .then((res) => {
        if (mounted) setSummary(res);
      })
      .catch(() => {
        if (mounted) setSummary(null);
      })
      .finally(() => {
        if (mounted) setSummaryLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedProjectId]);

  /* ---------- approvals fetchers (paginated) ---------- */
  const fetchApprovalsBackend = useCallback(
    async ({ page, page_size, search }) => {
      const params = {
        customer_id: customerId,
        approver_login_id: loginId,
        page,
        page_size,
      };
      if (search) params.search = search;
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (selectedProjectId) params.projectId = selectedProjectId;
      try {
        return await axiosInstance.get("/approvals", { params });
      } catch (error) {
        console.error("Error fetching approvals:", error);
        return { items: [], total: 0, page: 1, page_size: 30, total_pages: 1, has_next: false, has_previous: false };
      }
    },
    [customerId, loginId, statusFilter, selectedProjectId]
  );

  const fetchRequestsBackend = useCallback(
    async ({ page, page_size, search }) => {
      const params = {
        customer_id: customerId,
        requester_login_id: loginId,
        page,
        page_size,
      };
      if (search) params.search = search;
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (selectedProjectId) params.projectId = selectedProjectId;
      try {
        return await axiosInstance.get("/approvals", { params });
      } catch (error) {
        console.error("Error fetching requests:", error);
        return { items: [], total: 0, page: 1, page_size: 30, total_pages: 1, has_next: false, has_previous: false };
      }
    },
    [customerId, loginId, statusFilter, selectedProjectId]
  );

  const {
    data: approvals,
    pagination: approvalsPagination,
    searchInput: approvalsSearchInput,
    loading: approvalsLoading,
    goToPage: approvalsGoToPage,
    changePageSize: approvalsChangePageSize,
    handleSearchInputChange: approvalsHandleSearchInputChange,
    triggerSearch: approvalsTriggerSearch,
    refetch: approvalsRefetch,
  } = usePagination(fetchApprovalsBackend, 12, "", activeTab === "approvals", true);

  const {
    data: requests,
    pagination: requestsPagination,
    searchInput: requestsSearchInput,
    loading: requestsLoading,
    goToPage: requestsGoToPage,
    changePageSize: requestsChangePageSize,
    handleSearchInputChange: requestsHandleSearchInputChange,
    triggerSearch: requestsTriggerSearch,
    refetch: requestsRefetch,
  } = usePagination(fetchRequestsBackend, 12, "", activeTab === "requests", true);

  /* ---------- design iterations ---------- */
  const [designApprovals, setDesignApprovals] = useState([]);
  const [pastApprovedDesigns, setPastApprovedDesigns] = useState([]);
  const [designLoading, setDesignLoading] = useState(false);
  const [designFilter, setDesignFilter] = useState("all");

  const fetchDesignApprovals = useCallback(async () => {
    if (activeTab !== "designs") return;
    setDesignLoading(true);
    try {
      const params = {};
      const projectId = selectedProjectId || searchParams.get("project_id");
      const projectPhaseId = searchParams.get("project_phase_id");
      if (projectId) params.project_id = projectId;
      if (projectPhaseId) params.project_phase_id = projectPhaseId;
      const response = await axiosInstance.get(
        "/interior-designers/design-deliverables/pending-approval",
        { params }
      );
      const leads = Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [];
      setDesignApprovals(leads.map((l) => adaptLeadToApproval(l, "DEVELOPER_PENDING")));
    } catch (error) {
      console.error("Error fetching design approvals:", error);
      setDesignApprovals([]);
    } finally {
      setDesignLoading(false);
    }
  }, [activeTab, selectedProjectId, searchParams]);

  const fetchPastApprovedDesigns = useCallback(async () => {
    if (activeTab !== "designs") return;
    try {
      const params = {};
      const projectId = selectedProjectId || searchParams.get("project_id");
      const projectPhaseId = searchParams.get("project_phase_id");
      if (projectId) params.project_id = projectId;
      if (projectPhaseId) params.project_phase_id = projectPhaseId;
      const response = await axiosInstance.get(
        "/interior-designers/design-deliverables/past-approved",
        { params }
      );
      const leads = Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [];
      setPastApprovedDesigns(leads.map((l) => adaptLeadToApproval(l, "DEVELOPER_APPROVED")));
    } catch (error) {
      console.error("Error fetching past approved designs:", error);
      setPastApprovedDesigns([]);
    }
  }, [activeTab, selectedProjectId, searchParams]);

  useEffect(() => {
    fetchDesignApprovals();
  }, [fetchDesignApprovals]);
  useEffect(() => {
    if (activeTab === "designs") fetchPastApprovedDesigns();
  }, [activeTab, fetchPastApprovedDesigns]);

  const allDesigns = useMemo(() => {
    const pending = (designApprovals || []).map((d) => ({ ...d, _source: "pending" }));
    const past = (pastApprovedDesigns || []).map((d) => ({ ...d, _source: "past-approved" }));
    return [...pending, ...past];
  }, [designApprovals, pastApprovedDesigns]);

  const filteredDesigns = useMemo(() => {
    if (designFilter === "all") return allDesigns;
    const map = {
      pending: "DEVELOPER_PENDING",
      approved: "DEVELOPER_APPROVED",
      rejected: "DEVELOPER_REJECTED",
    };
    return allDesigns.filter((d) => d.developerApprovalStatus === map[designFilter]);
  }, [allDesigns, designFilter]);

  /* ---------- design media presign ---------- */
  useEffect(() => {
    if (!isDrawerOpen || activeTab !== "designs" || !selectedApproval?.media) return;
    const bySection = normalizeMediaBySection(selectedApproval.media);
    const s3Keys = [];
    Object.values(bySection || {})
      .flat()
      .forEach((item) => {
        const key =
          typeof item === "string"
            ? item
            : item?.s3_key || item?.s3Key || item?.key || item?.media_key || item?.mediaKey || item?.file_key;
        if (!key || typeof key !== "string") return;
        if (item && typeof item === "object" && (item?.url || item?.downloadUrl)) return;
        if (key.startsWith("/9j/") || key.startsWith("iVBORw0KGgo") || key.startsWith("data:")) return;
        s3Keys.push(key);
      });
    const uniqueKeys = [...new Set(s3Keys)];
    if (uniqueKeys.length === 0) {
      setDesignMediaUrls({});
      return;
    }
    let cancelled = false;
    setDesignMediaUrlsLoading(true);
    getPresignedDownloadUrls(uniqueKeys, axiosInstance)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        res.forEach((presigned, i) => {
          const k = uniqueKeys[i];
          const url = presigned.download_url || presigned.downloadUrl || presigned.url;
          if (url) map[k] = url;
        });
        setDesignMediaUrls(map);
      })
      .catch(() => {
        if (!cancelled) setDesignMediaUrls({});
      })
      .finally(() => {
        if (!cancelled) setDesignMediaUrlsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isDrawerOpen, activeTab, selectedApproval?.media]);

  const getDesignImageSrc = (item) => {
    if (!item) return null;
    const s3Key =
      typeof item === "object" &&
      (item?.s3_key || item?.s3Key || item?.key || item?.media_key || item?.mediaKey || item?.file_key);
    if (typeof item === "object" && s3Key) {
      if (designMediaUrls[s3Key]) return designMediaUrls[s3Key];
      if (item.url || item.downloadUrl) return getMediaUrl(item) || item.url || item.downloadUrl;
      if (s3Key.startsWith("/9j/") || s3Key.startsWith("iVBORw0KGgo")) {
        return (s3Key.startsWith("/9j/") ? "data:image/jpeg;base64," : "data:image/png;base64,") + s3Key;
      }
    }
    if (typeof item === "object" && (item.content || item.base64)) {
      const raw = item.content || item.base64;
      const contentType = item.contentType || item.content_type || "image/jpeg";
      if (raw.startsWith("data:")) return raw;
      if (raw.startsWith("/9j/") || raw.startsWith("iVBORw")) return `data:${contentType};base64,${raw}`;
      return getMediaUrl({ content: raw, contentType }) || `data:${contentType};base64,${raw}`;
    }
    if (typeof item === "string") {
      if (item.startsWith("http") || item.startsWith("data:")) return item;
      if (item.startsWith("/9j/") || item.startsWith("iVBORw0KGgo") || item.length > 100) {
        return item.startsWith("/9j/") ? `data:image/jpeg;base64,${item}` : `data:image/png;base64,${item}`;
      }
    }
    return null;
  };

  /* ---------- drawer + actions ---------- */
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setPdfUrl(null);
    setSelectedApproval(null);
    setComment("");
    setDesignMediaUrls({});
  };

  const handleView = (item) => {
    setSelectedApproval(item);
    setComment(activeTab === "designs" ? item?.developerApprovalNotes || "" : item?.comment || "");
    setPdfUrl(item?.attachment || null);
    openDrawer();
  };

  const handleApprovalAction = async (approvalId, newStatus) => {
    setActionLoading(true);
    try {
      const body = {
        approvalTypeId: selectedApproval?.approvalType?.approvalTypeId,
        requesterLoginId: selectedApproval?.requesterLogin?.loginId,
        approverLoginId: selectedApproval?.approverLogin?.loginId,
        userId: selectedApproval?.user?.userId,
        customerId: selectedApproval?.customer?.customerId,
        projectId: selectedApproval?.projectPhase?.project?.projectId,
        projectPhaseId: selectedApproval?.projectPhase?.projectPhaseId,
        propertyId: selectedApproval?.property?.propertyId,
        status: newStatus,
        comment,
        approvalDatetime: new Date().toISOString(),
        attachment: selectedApproval?.attachment,
      };
      await axiosInstance.put(`/approvals?approvalId=${approvalId}&sendNotification=false`, body);
      toast.success(`Approval ${newStatus.toLowerCase()}!`);
      approvalsRefetch();
      closeDrawer();
    } catch (error) {
      console.error(`Error ${newStatus.toLowerCase()} approval:`, error);
      toast.error(`Failed to ${newStatus.toLowerCase()} approval`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendNotification = async (approvalId) => {
    setActionLoading(true);
    try {
      const body = {
        approvalTypeId: selectedApproval?.approvalType?.approvalTypeId,
        requesterLoginId: selectedApproval?.requesterLogin?.loginId,
        approverLoginId: selectedApproval?.approverLogin?.loginId,
        userId: selectedApproval?.user?.userId,
        customerId: selectedApproval?.customer?.customerId,
        projectId: selectedApproval?.projectPhase?.project?.projectId,
        projectPhaseId: selectedApproval?.projectPhase?.projectPhaseId,
        propertyId: selectedApproval?.property?.propertyId,
        status: selectedApproval?.status,
        comment: selectedApproval?.comment,
        approvalDatetime: selectedApproval?.approvalDatetime,
        attachment: selectedApproval?.attachment,
      };
      await axiosInstance.put(`/approvals?approvalId=${approvalId}&sendNotification=true`, body);
      toast.success("Notification sent!");
      requestsRefetch();
      closeDrawer();
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Failed to send notification.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeveloperDesignApproval = async (iterationId, newStatus) => {
    setActionLoading(true);
    try {
      // Developer decision now lives on the lead: approve → FINALIZED, else → CHANGES_REQUESTED.
      const idLeadId = selectedApproval?.idLeadId || selectedApproval?.interiorDesignerLeadId;
      await axiosInstance.post(`/interior-designers/leads/${idLeadId}/design/developer-review`, {
        approve: newStatus === "DEVELOPER_APPROVED",
        notes: comment || selectedApproval.developerApprovalNotes || null,
      });
      toast.success(`Design ${newStatus === "DEVELOPER_APPROVED" ? "approved" : "rejected"}!`);
      fetchDesignApprovals();
      fetchPastApprovedDesigns();
      closeDrawer();
    } catch (error) {
      console.error("Error updating design approval:", error);
      toast.error("Failed to update design approval");
    } finally {
      setActionLoading(false);
    }
  };

  /* ---------- derived ---------- */
  const selectedProjectLabel =
    selectedProjectId === ""
      ? "All projects"
      : projectOptions.find((p) => String(p.projectId) === String(selectedProjectId))?.projectName ||
        "Project";

  const currentSearch = activeTab === "approvals" ? approvalsSearchInput : requestsSearchInput;
  const setCurrentSearch =
    activeTab === "approvals" ? approvalsHandleSearchInputChange : requestsHandleSearchInputChange;
  const triggerCurrentSearch =
    activeTab === "approvals" ? approvalsTriggerSearch : requestsTriggerSearch;
  const currentLoading =
    activeTab === "approvals" ? approvalsLoading : activeTab === "requests" ? requestsLoading : designLoading;
  const currentPagination = activeTab === "approvals" ? approvalsPagination : requestsPagination;
  const currentGoToPage = activeTab === "approvals" ? approvalsGoToPage : requestsGoToPage;
  const currentChangePageSize =
    activeTab === "approvals" ? approvalsChangePageSize : requestsChangePageSize;

  const currentRows = useMemo(() => {
    if (activeTab === "approvals") return approvals || [];
    if (activeTab === "requests") return requests || [];
    if (activeTab === "designs") return filteredDesigns || [];
    return [];
  }, [activeTab, approvals, requests, filteredDesigns]);

  /* ---------- card rendering ---------- */
  const renderApprovalRow = (a) => (
    <button
      key={a.approvalId}
      type="button"
      onClick={() => handleView(a)}
      className="group w-full text-left bg-white border border-slate-200 hover:border-flamingo-200 hover:shadow-md transition-all rounded-2xl p-4 flex items-start gap-4"
    >
      <div className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-flamingo-50 flex items-center justify-center shrink-0">
        <ClipboardCheck size={18} className="text-slate-600 group-hover:text-flamingo-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {a.approvalType?.approvalTypeName || "Approval"}
            </p>
            <p className="text-xs text-slate-500 truncate mt-0.5">
              {a.projectPhase?.project?.projectName || "—"}
              {a.property?.propertyNo ? ` · Unit ${a.property.propertyNo}` : ""}
            </p>
          </div>
          <StatusPill status={a.status} />
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span>
            User:{" "}
            <span className="text-slate-700 font-medium">
              {a.user?.firstName} {a.user?.lastName || ""}
            </span>
          </span>
          <span>
            Requester:{" "}
            <span className="text-slate-700 font-medium">{a.requesterLogin?.name || "—"}</span>
          </span>
          <span>
            Approver:{" "}
            <span className="text-slate-700 font-medium">{a.approverLogin?.name || "—"}</span>
          </span>
        </div>
        {a.comment && (
          <p className="mt-2 text-xs text-slate-600 line-clamp-2 bg-slate-50 rounded-md px-2 py-1.5">
            {a.comment}
          </p>
        )}
      </div>
      <ChevronRight size={16} className="text-slate-400 group-hover:text-flamingo-600 shrink-0 mt-2" />
    </button>
  );

  const renderDesignRow = (d) => {
    const label = DESIGN_STATUS_LABEL[d.developerApprovalStatus] || "PENDING";
    return (
      <button
        key={d.designIterationId || d.id}
        type="button"
        onClick={() => handleView(d)}
        className="group w-full text-left bg-white border border-slate-200 hover:border-flamingo-200 hover:shadow-md transition-all rounded-2xl p-4 flex items-start gap-4"
      >
        <div className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-flamingo-50 flex items-center justify-center shrink-0">
          <Palette size={18} className="text-slate-600 group-hover:text-flamingo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                Iteration #{d.iterationNumber}
              </p>
              <p className="text-xs text-slate-500 truncate mt-0.5">
                Est. {fmtINR(d.estimatedPrice)}
                {d.isFinal ? " · Final iteration" : ""}
              </p>
            </div>
            <StatusPill status={label} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            <span>
              Status:{" "}
              <span className="text-slate-700 font-medium">{d.iterationStatus || "—"}</span>
            </span>
            {d.feedbackDate && <span>Feedback: {fmtDate(d.feedbackDate)}</span>}
          </div>
          {d.designerNotes && (
            <p className="mt-2 text-xs text-slate-600 line-clamp-2 bg-slate-50 rounded-md px-2 py-1.5">
              {d.designerNotes}
            </p>
          )}
        </div>
        <ChevronRight size={16} className="text-slate-400 group-hover:text-flamingo-600 shrink-0 mt-2" />
      </button>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {/* Hero band */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <ClipboardCheck size={12} /> Workflow
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">Approvals</h1>
            <p className="text-xs text-slate-300 mt-1 flex items-center gap-1.5">
              <CalendarDays size={12} />
              {selectedProjectLabel}
              <span className="mx-1.5 text-slate-500">•</span>
              {TABS.find((t) => t.value === activeTab)?.description}
            </p>
          </div>
          <div className="flex flex-col gap-1.5 min-w-[14rem]">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">Filter by project</label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none"
            >
              <option value="" className="text-slate-900">
                All projects
              </option>
              {projectOptions.map((p) => (
                <option key={p.projectId} value={p.projectId} className="text-slate-900">
                  {p.projectName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryLoading ? (
          <>
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
          </>
        ) : (
          <>
            <KpiTile
              icon={Inbox}
              label="Waiting on you"
              value={fmtInt(summary?.assignedToMePending)}
              subtitle={
                summary?.assignedToMePending > 0
                  ? "Action needed"
                  : "Inbox empty"
              }
              accent={summary?.assignedToMePending > 0 ? "amber" : "emerald"}
            />
            <KpiTile
              icon={Send}
              label="Awaiting others"
              value={fmtInt(summary?.requestedByMePending)}
              subtitle="Requests you've sent"
              accent="indigo"
            />
            <KpiTile
              icon={CheckCircle2}
              label="Approved this week"
              value={fmtInt(summary?.approvedLast7Days)}
              subtitle={`${fmtInt(summary?.rejectedLast7Days)} rejected`}
              accent="emerald"
            />
            <KpiTile
              icon={Palette}
              label="Design iterations"
              value={fmtInt(summary?.designsPending)}
              subtitle="Pending developer review"
              accent="flamingo"
            />
          </>
        )}
      </div>

      {/* Tab nav + filter strip */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 inline-flex gap-1 flex-wrap">
          {TABS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setActiveTab(value)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === value
                  ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* Status filter — hidden on demand letter (its own filter), designs (uses designFilter),
            and interior executions (self-contained list) */}
        {activeTab !== "demandletter" && activeTab !== "designs" && activeTab !== "interior_executions" && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={currentSearch}
                onChange={(e) => setCurrentSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") triggerCurrentSearch();
                }}
                placeholder="Search…"
                className="pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none w-56"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none"
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {activeTab === "designs" && (
          <select
            value={designFilter}
            onChange={(e) => setDesignFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none"
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        )}
      </div>

      {/* Body */}
      {activeTab === "interior_executions" ? (
        <AgreementRequestsList />
      ) : activeTab === "demandletter" ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <DemandLetterApproval hideHeader={true} />
        </div>
      ) : (
        <>
          {/* Card list */}
          {currentLoading ? (
            <div className="grid gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4"
                >
                  <Skeleton circle height={40} width={40} />
                  <div className="flex-1">
                    <Skeleton height={14} width="40%" />
                    <Skeleton height={12} width="70%" className="mt-2" />
                  </div>
                  <Skeleton width={60} height={20} />
                </div>
              ))}
            </div>
          ) : currentRows.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 px-6 py-16 text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 size={28} className="text-emerald-500" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">
                {activeTab === "approvals"
                  ? "Inbox zero"
                  : activeTab === "requests"
                  ? "No requests to track"
                  : "No design iterations to review"}
              </h3>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                {activeTab === "approvals"
                  ? "Nothing waiting on your decision right now."
                  : activeTab === "requests"
                  ? "You haven't raised any approval requests in this scope."
                  : "All design iterations have been reviewed."}
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {activeTab === "designs"
                ? currentRows.map(renderDesignRow)
                : currentRows.map(renderApprovalRow)}
            </div>
          )}

          {/* Pagination — only for paginated tabs (designs is a flat list) */}
          {activeTab !== "designs" && currentPagination?.total > 0 && (
            <div className="mt-auto pt-4 border-t border-slate-200">
              <PaginationControls
                pagination={currentPagination}
                onPageChange={currentGoToPage}
                onPageSizeChange={currentChangePageSize}
                itemName={activeTab === "approvals" ? "approvals" : "requests"}
                variant="list"
                showInfo={true}
              />
            </div>
          )}
        </>
      )}

      {/* Drawer */}
      <Drawer open={isDrawerOpen} onClose={closeDrawer} size="xl" className="z-40">
        <Drawer.Overlay>
          <Drawer.Panel className="w-full md:w-[700px] overflow-y-auto flex flex-col h-full">
            {/* Drawer header — slate gradient to match the hero */}
            <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-600 text-white sticky top-0 z-10 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-slate-300">
                  {activeTab === "designs" ? "Design iteration" : "Approval"}
                </p>
                <h3 className="text-lg font-semibold leading-tight truncate">
                  {activeTab === "designs"
                    ? `Iteration #${selectedApproval?.iterationNumber || "—"}`
                    : selectedApproval?.approvalType?.approvalTypeName || "Approval"}
                </h3>
                <div className="mt-1">
                  {activeTab === "designs" ? (
                    <StatusPill
                      status={DESIGN_STATUS_LABEL[selectedApproval?.developerApprovalStatus] || "PENDING"}
                    />
                  ) : (
                    <StatusPill status={selectedApproval?.status || "PENDING"} />
                  )}
                </div>
              </div>
              <CloseButton onClick={closeDrawer} />
            </div>

            {selectedApproval && (
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-slate-50/40">
                {activeTab === "designs" ? (
                  <>
                    {/* Iteration info card */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Iteration info
                        </p>
                      </div>
                      <dl className="grid grid-cols-2 divide-x divide-slate-100">
                        <div className="px-4 py-3">
                          <dt className="text-xs text-slate-500">Estimated price</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5">
                            {fmtINR(selectedApproval?.estimatedPrice)}
                          </dd>
                        </div>
                        <div className="px-4 py-3">
                          <dt className="text-xs text-slate-500">Final iteration</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5">
                            {selectedApproval?.isFinal ? "Yes" : "No"}
                          </dd>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-100">
                          <dt className="text-xs text-slate-500">Iteration status</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5">
                            {selectedApproval?.iterationStatus || "—"}
                          </dd>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-100">
                          <dt className="text-xs text-slate-500">Feedback date</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5">
                            {fmtDate(selectedApproval?.feedbackDate)}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Notes & feedback */}
                    {(selectedApproval?.designerNotes || selectedApproval?.buyerFeedback) && (
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                            Notes & feedback
                          </p>
                        </div>
                        <div className="p-4 space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-slate-500 mb-1">Designer notes</p>
                            <p className="text-sm text-slate-800 bg-slate-50 rounded-lg p-3 border border-slate-100 min-h-[48px]">
                              {selectedApproval?.designerNotes || "—"}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500 mb-1">Buyer feedback</p>
                            <p className="text-sm text-slate-800 bg-slate-50 rounded-lg p-3 border border-slate-100 min-h-[48px]">
                              {selectedApproval?.buyerFeedback || "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Design media gallery */}
                    {selectedApproval?.media != null &&
                      (() => {
                        const bySection = normalizeMediaBySection(selectedApproval.media);
                        const sectionOrder = getSectionDisplayOrder();
                        const sectionsWithMedia = sectionOrder.filter(
                          (sec) => bySection[sec]?.length > 0
                        );
                        const uncategorized = Object.keys(bySection).filter(
                          (k) => !sectionOrder.includes(k)
                        );
                        const allKeys = [...sectionsWithMedia, ...uncategorized];
                        const allItems = allKeys.flatMap((sec) =>
                          (bySection[sec] || []).map((item) =>
                            typeof item === "string"
                              ? { key: item, section: sec }
                              : { ...item, section: sec }
                          )
                        );
                        return (
                          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                Design media
                              </p>
                            </div>
                            <div className="p-4">
                              {allItems.length === 0 ? (
                                <div className="flex items-center justify-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                                  <ImageIcon className="mr-2" size={20} />
                                  <span className="text-sm">No media for this iteration</span>
                                </div>
                              ) : designMediaUrlsLoading ? (
                                <div className="flex items-center justify-center py-12 text-slate-500">
                                  <Loader2 className="animate-spin mr-2" size={20} />
                                  <span className="text-sm">Loading media…</span>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {allItems.map((item, idx) => {
                                    const src = getDesignImageSrc(item);
                                    const label = item?.section
                                      ? `${item.section} ${idx + 1}`
                                      : `Design ${idx + 1}`;
                                    return (
                                      <div
                                        key={idx}
                                        className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50"
                                      >
                                        {src ? (
                                          <img
                                            src={src}
                                            alt={label}
                                            className="w-full h-auto max-h-64 object-contain"
                                          />
                                        ) : (
                                          <div className="flex items-center justify-center h-40">
                                            <ImageIcon className="text-slate-400" size={32} />
                                          </div>
                                        )}
                                        <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between">
                                          <span className="text-xs text-slate-600 truncate">
                                            {label}
                                          </span>
                                          {src && (
                                            <a
                                              href={src}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-1 text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
                                            >
                                              <Download size={12} /> View
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                    {/* Developer notes textarea — only when pending */}
                    {selectedApproval?.developerApprovalStatus === "DEVELOPER_PENDING" && (
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                            Developer approval notes
                          </p>
                        </div>
                        <div className="p-4">
                          <textarea
                            rows={3}
                            className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-500 outline-none resize-none"
                            placeholder="Add your notes…"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Approval info */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Approval info
                        </p>
                      </div>
                      <dl className="grid grid-cols-2 divide-x divide-slate-100">
                        <div className="px-4 py-3">
                          <dt className="text-xs text-slate-500">Project</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5 truncate">
                            {selectedApproval?.projectPhase?.project?.projectName || "—"}
                          </dd>
                        </div>
                        <div className="px-4 py-3">
                          <dt className="text-xs text-slate-500">Property</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5">
                            {selectedApproval?.property?.propertyNo || "—"}
                          </dd>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-100">
                          <dt className="text-xs text-slate-500">Requester</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5 truncate">
                            {selectedApproval?.requesterLogin?.name || "—"}
                          </dd>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-100">
                          <dt className="text-xs text-slate-500">User</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5 truncate">
                            {selectedApproval?.user?.firstName || "—"}
                          </dd>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-100 col-span-2">
                          <dt className="text-xs text-slate-500">Approver</dt>
                          <dd className="text-sm font-semibold text-slate-900 mt-0.5">
                            {selectedApproval?.approverLogin?.name || "—"}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Comment textarea (approvals tab only) */}
                    {activeTab === "approvals" && selectedApproval?.status === "PENDING" && (
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                            Comments
                          </p>
                        </div>
                        <div className="p-4">
                          <textarea
                            rows={3}
                            className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-500 outline-none resize-none"
                            placeholder="Add your comments…"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                          />
                        </div>
                      </div>
                    )}

                    {/* Existing comment when already decided */}
                    {selectedApproval?.comment && selectedApproval?.status !== "PENDING" && (
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                            Comments
                          </p>
                        </div>
                        <div className="p-4 text-sm text-slate-800 whitespace-pre-wrap">
                          {selectedApproval.comment}
                        </div>
                      </div>
                    )}

                    {/* Document preview */}
                    {pdfUrl && (
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                            Document
                          </p>
                          <button
                            type="button"
                            onClick={() => setIsCostSheetModalOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-flamingo-50 text-flamingo-700 border border-flamingo-200 hover:bg-flamingo-100 transition-colors"
                          >
                            <Eye size={13} /> View cost sheet
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Sticky action bar at bottom */}
            {selectedApproval && (
              <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center justify-end gap-2">
                {activeTab === "approvals" && selectedApproval?.status === "PENDING" && (
                  <>
                    <OutlineButton
                      onClick={() => handleApprovalAction(selectedApproval.approvalId, "REJECTED")}
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <Loader2 className="animate-spin" size={15} />
                      ) : (
                        <X size={15} />
                      )}
                      Decline
                    </OutlineButton>
                    <PrimaryButton
                      onClick={() => handleApprovalAction(selectedApproval.approvalId, "APPROVED")}
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <Loader2 className="animate-spin" size={15} />
                      ) : (
                        <Check size={15} />
                      )}
                      Approve
                    </PrimaryButton>
                  </>
                )}
                {activeTab === "designs" &&
                  selectedApproval?.developerApprovalStatus === "DEVELOPER_PENDING" && (
                    <>
                      <OutlineButton
                        onClick={() =>
                          handleDeveloperDesignApproval(
                            selectedApproval.designIterationId,
                            "DEVELOPER_REJECTED"
                          )
                        }
                        disabled={actionLoading}
                      >
                        {actionLoading ? (
                          <Loader2 className="animate-spin" size={15} />
                        ) : (
                          <X size={15} />
                        )}
                        Decline
                      </OutlineButton>
                      <PrimaryButton
                        onClick={() =>
                          handleDeveloperDesignApproval(
                            selectedApproval.designIterationId,
                            "DEVELOPER_APPROVED"
                          )
                        }
                        disabled={actionLoading}
                      >
                        {actionLoading ? (
                          <Loader2 className="animate-spin" size={15} />
                        ) : (
                          <Check size={15} />
                        )}
                        Approve
                      </PrimaryButton>
                    </>
                  )}
                {activeTab === "requests" &&
                  selectedApproval?.status === "PENDING" &&
                  !selectedApproval?.isNotificationSent && (
                    <button
                      onClick={() => handleSendNotification(selectedApproval.approvalId)}
                      disabled={actionLoading}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-flamingo-500 text-white hover:bg-flamingo-600 transition-colors disabled:opacity-50"
                    >
                      {actionLoading ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
                      Send notification
                    </button>
                  )}
              </div>
            )}
          </Drawer.Panel>
        </Drawer.Overlay>
      </Drawer>

      {/* Cost sheet modal */}
      {isCostSheetModalOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => setIsCostSheetModalOpen(false)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-xl">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Cost sheet</h3>
                <CloseButton onClick={() => setIsCostSheetModalOpen(false)} />
              </div>
              <div className="p-4">
                <iframe
                  srcDoc={selectedApproval?.attachment}
                  title="Cost sheet"
                  width="100%"
                  height="600px"
                  style={{ border: "none" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Approval;
